import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { IResourceCategory } from 'src/models/Project';
import { client } from 'src/utils/api';
import en from 'src/messages/en.json';
import { setError } from 'src/redux/features/common/commonSlice';

export const fetchActivities: any = createAsyncThunk(
  'session/fethcActivities',
  async (_data, { rejectWithValue, dispatch }) => {
    try {
      const response = await client.get('Activity');

      return response.data;
    } catch (err) {
      console.log(err);
      dispatch(setError(en.Common.Error.fetchData));

      return rejectWithValue(err);
    }
  },
);

export const fetchAssignedProjects: any = createAsyncThunk(
  'session/fetchAssignedProjects',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await client.get(`Project/assigned`, {
        // body: { resource },
      });

      return response.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const fetchProjects: any = createAsyncThunk(
  'session/fetchProjects',
  async (_data, { rejectWithValue }) => {
    try {
      const response = await client.get(`Project`);

      return response.data;
    } catch (err) {
      console.log(en.Common.Error.fetchData);

      return rejectWithValue(err);
    }
  },
);

export const fetchCategoryActivityOfResource: any = createAsyncThunk(
  'session/fetchCategoryActivityOfResource',
  async ({ Email }: any, { rejectWithValue, dispatch }) => {
    try {
      const response = await client.get(`Project/AssignedCategories/${Email}`);

      return response.data as IResourceCategory;
    } catch (err) {
      dispatch(setError(en.Common.Error.fetchData));

      return rejectWithValue(err);
    }
  },
);

export const updateCategoryActivityStatus: any = createAsyncThunk(
  'session/updateCategoryActivityStatus',
  async ({ id, payload }: any, { rejectWithValue, dispatch }) => {
    try {
      const reqObj = payload.activities.map((item: IResourceCategory) => item);

      const response = await client.post(
        `Project/update/categoryStatus`,
        reqObj,
      );

      return response.data;
    } catch (err) {
      dispatch(setError(en.Common.Error.saveChanges));

      return rejectWithValue(err);
    }
  },
);

enum Status {
  idle = 'idle',
  loading = 'loading',
  succeeded = 'succeeded',
  failed = 'failed',
}

const defaultState = {
  preferences: {
    teamTime: false,
    showMenu: false,
  },
  activities: {
    data: [],
    error: '',
    status: Status.idle,
    loading: false,
  },
  assignedProjects: {
    data: [],
    error: '',
    status: Status.idle,
    loading: false,
  },
  searchProjects: {
    data: [],
    error: '',
    status: Status.idle,
    loading: false,
  },
  searchCategoryActivity: {
    data: [],
    error: '',
    status: Status.idle,
    loading: false,
  },
};

const sessionSlice = createSlice({
  name: 'session',
  initialState: { ...defaultState },
  reducers: {
    resetSession: (state) => {
      state.activities = { ...defaultState.activities };
    },
    setTeamTime: (state, action) => {
      state.preferences = { ...state.preferences, teamTime: action.payload };
    },
    setOffCanvasMenu: (state) => {
      state.preferences = {
        ...state.preferences,
        showMenu: !state.preferences.showMenu,
      };
    },
  },
  extraReducers: {
    [fetchActivities.pending]: (state) => {
      state.activities.loading = true;
      state.activities.status = Status.loading;
    },
    [fetchActivities.fulfilled]: (state, action) => {
      state.activities.loading = false;
      state.activities.data = action.payload;
      state.activities.status = Status.succeeded;
    },
    [fetchActivities.rejected]: (state, action) => {
      state.activities.loading = false;
      state.activities.status = Status.failed;
      state.activities.error = action.error.message;
    },
    [fetchAssignedProjects.pending]: (state) => {
      state.assignedProjects.loading = true;
      state.assignedProjects.status = Status.loading;
    },
    [fetchAssignedProjects.fulfilled]: (state, action) => {
      state.assignedProjects.loading = false;
      state.assignedProjects.data = action.payload;
      state.assignedProjects.status = Status.succeeded;
    },
    [fetchAssignedProjects.rejected]: (state, action) => {
      state.assignedProjects.loading = false;
      state.assignedProjects.status = Status.failed;
      state.assignedProjects.data = [];
      state.assignedProjects.error = action.error.message;
    },
    [fetchProjects.pending]: (state) => {
      state.searchProjects.loading = true;
      state.searchProjects.status = Status.loading;
    },
    [fetchProjects.fulfilled]: (state, action) => {
      state.searchProjects.loading = false;
      state.searchProjects.data = action.payload;
      state.searchProjects.status = Status.succeeded;
    },
    [fetchProjects.rejected]: (state, action) => {
      state.searchProjects.loading = false;
      state.searchProjects.status = Status.failed;
      state.searchProjects.data = [];
      state.searchProjects.error = action.error.message;
    },
    [fetchCategoryActivityOfResource.pending]: (state) => {
      state.searchCategoryActivity.loading = true;
      state.searchCategoryActivity.status = Status.loading;
    },
    [fetchCategoryActivityOfResource.fulfilled]: (state, action) => {
      state.searchCategoryActivity.loading = false;
      state.searchCategoryActivity.data = action.payload;
      state.searchCategoryActivity.status = Status.succeeded;
    },
    [fetchCategoryActivityOfResource.rejected]: (state, action) => {
      state.searchCategoryActivity.loading = false;
      state.searchCategoryActivity.status = Status.failed;
      state.searchCategoryActivity.data = [];
      state.searchCategoryActivity.error = action.error.message;
    },
  },
});

export const getPreferences = (state) => {
  return { ...state.session.preferences };
};

export const getActivities = (state) => {
  return {
    data: state.session.activities.data,
    status: state.session.activities.status,
    loading: state.session.activities.loading,
  };
};

export const getAssignedProjects = (state) => {
  return {
    data: state.session.assignedProjects.data,
    status: state.session.assignedProjects.status,
    loading: state.session.assignedProjects.loading,
  };
};

export const getProjects = (state) => {
  return {
    data: state.session.searchProjects.data,
    status: state.session.searchProjects.status,
    loading: state.session.searchProjects.loading,
  };
};
export const getCategoryActivity = (state) => {
  return {
    data: state.session.searchCategoryActivity.data,
    status: state.session.searchCategoryActivity.status,
    loading: state.session.searchCategoryActivity.loading,
  };
};

const actions = sessionSlice.actions;

export { actions };
export default sessionSlice.reducer;
